<script setup>
import { toastError, toastSuccess } from '@js/utils/toast';

const { variables } = defineProps({
    variables: {
        type: Object,
        required: true,
    },
});

function copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(
        () => {
            toastSuccess('Copié avec succès.');
        },
        () => {
            toastError('Une erreur est survenue.');
        },
    );
}
</script>

<template>
    <ul class="!ml-0 !list-none">
        <li
            v-for="(variable, variableKey) in variables"
            :key="variableKey"
            class="border-l border-gray-400 pl-2"
        >
            <button
                type="button"
                @click="copyToClipboard('$' + variableKey)"
                class="font-bold text-gray-800 hover:underline"
            >
                ${{ variableKey }}
            </button>
            <span class="ml-2 text-xs text-slate-500">{{ variable.type }}</span>
            <span class="ml-2 text-xs text-slate-400">{{
                variable.description
            }}</span>

            <ul
                v-if="variable.properties"
                class="!ml-12 !list-none border-l border-gray-400"
            >
                <li
                    v-for="(property, propertyKey) in variable.properties"
                    :key="propertyKey"
                    class="pl-2"
                >
                    <button
                        type="button"
                        @click="
                            copyToClipboard(
                                '$' + variableKey + '->' + propertyKey,
                            )
                        "
                        class="font-bold text-gray-800 hover:underline"
                    >
                        ${{ variableKey }}->{{ propertyKey }}
                    </button>
                    <span class="ml-2 text-xs text-slate-500">{{
                        property.type
                    }}</span>
                    <span class="ml-2 text-xs text-slate-400">{{
                        property.description
                    }}</span>

                    <ul
                        v-if="property.properties"
                        class="!ml-12 !list-none border-l border-gray-400"
                    >
                        <li
                            v-for="(
                                subProperty, subPropertyKey
                            ) in property.properties"
                            :key="subPropertyKey"
                            class="pl-2"
                        >
                            <button
                                type="button"
                                @click="
                                    copyToClipboard(
                                        '$' +
                                            variableKey +
                                            '->' +
                                            propertyKey +
                                            '->' +
                                            subPropertyKey,
                                    )
                                "
                                class="font-bold text-gray-800 hover:underline"
                            >
                                ${{ variableKey }}->{{ propertyKey }}->{{
                                    subPropertyKey
                                }}
                            </button>
                            <span class="ml-2 text-xs text-slate-500">{{
                                subProperty.type
                            }}</span>
                            <span class="ml-2 text-xs text-slate-400">{{
                                subProperty.description
                            }}</span>

                            <ul
                                v-if="subProperty.properties"
                                class="!ml-12 !list-none border-l border-gray-400"
                            >
                                <li
                                    v-for="(
                                        subSubProperty, subSubPropertyKey
                                    ) in subProperty.properties"
                                    :key="subSubPropertyKey"
                                    class="pl-2"
                                >
                                    <button
                                        type="button"
                                        @click="
                                            copyToClipboard(
                                                '$' +
                                                    variableKey +
                                                    '->' +
                                                    propertyKey +
                                                    '->' +
                                                    subPropertyKey,
                                            )
                                        "
                                        class="font-bold text-gray-800 hover:underline"
                                    >
                                        ${{ variableKey }}->{{
                                            propertyKey
                                        }}->{{ subPropertyKey }}->{{
                                            subSubPropertyKey
                                        }}
                                    </button>
                                    <span class="ml-2 text-xs text-slate-500">{{
                                        subSubProperty.type
                                    }}</span>
                                    <span class="ml-2 text-xs text-slate-400">{{
                                        subSubProperty.description
                                    }}</span>
                                </li>
                            </ul>
                        </li>
                    </ul>

                    <ul
                        v-if="property.itemProperties"
                        class="!ml-12 !list-none border-l border-gray-400"
                    >
                        <li
                            v-for="(
                                sub1ItemProperty, sub1ItemPropertyKey
                            ) in property.itemProperties"
                            :key="sub1ItemPropertyKey"
                            class="pl-2"
                        >
                            <span class="font-bold text-gray-800"
                                >${{ variableKey }}->{{ propertyKey }}[ ]->{{
                                    sub1ItemPropertyKey
                                }}</span
                            >
                            <span class="ml-2 text-xs text-slate-500">{{
                                sub1ItemProperty.type
                            }}</span>
                            <span class="ml-2 text-xs text-slate-400">{{
                                sub1ItemProperty.description
                            }}</span>
                            <ul
                                v-if="sub1ItemProperty.properties"
                                class="!ml-12 !list-none border-l border-gray-400"
                            >
                                <li
                                    v-for="(
                                        sub2ItemProperty, sub2ItemPropertyKey
                                    ) in sub1ItemProperty.properties"
                                    :key="sub2ItemPropertyKey"
                                    class="pl-2"
                                >
                                    <span class="font-bold text-gray-800"
                                        >${{ variableKey }}->{{ propertyKey }}[
                                        ]->{{ sub1ItemPropertyKey }}->{{
                                            sub2ItemPropertyKey
                                        }}</span
                                    >
                                    <span class="ml-2 text-xs text-slate-500">{{
                                        sub2ItemProperty.type
                                    }}</span>
                                    <span class="ml-2 text-xs text-slate-400">{{
                                        sub2ItemProperty.description
                                    }}</span>
                                    <ul
                                        v-if="sub2ItemProperty.properties"
                                        class="!ml-12 !list-none border-l border-gray-400"
                                    >
                                        <li
                                            v-for="(
                                                sub3ItemProperty,
                                                sub3ItemPropertyKey
                                            ) in sub2ItemProperty.properties"
                                            :key="sub3ItemPropertyKey"
                                            class="pl-2"
                                        >
                                            <span
                                                class="font-bold text-gray-800"
                                                >${{ variableKey }}->{{
                                                    propertyKey
                                                }}[ ]->{{
                                                    sub1ItemPropertyKey
                                                }}->{{
                                                    sub2ItemPropertyKey
                                                }}->{{
                                                    sub3ItemPropertyKey
                                                }}</span
                                            >
                                            <span
                                                class="ml-2 text-xs text-slate-500"
                                                >{{
                                                    sub3ItemProperty.type
                                                }}</span
                                            >
                                            <span
                                                class="ml-2 text-xs text-slate-400"
                                                >{{
                                                    sub3ItemProperty.description
                                                }}</span
                                            >
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                        </li>
                    </ul>
                </li>
            </ul>

            <ul
                v-if="variable.itemProperties"
                class="!ml-12 !list-none border-l border-gray-400"
            >
                <li
                    v-for="(
                        itemProperty, itemPropertyKey
                    ) in variable.itemProperties"
                    :key="itemPropertyKey"
                    class="pl-2"
                >
                    <span class="font-bold text-gray-800"
                        >${{ variableKey }}[ ]->{{ itemPropertyKey }}</span
                    >
                    <span class="ml-2 text-xs text-slate-500">{{
                        itemProperty.type
                    }}</span>
                    <span class="ml-2 text-xs text-slate-400">{{
                        itemProperty.description
                    }}</span>

                    <ul
                        v-if="itemProperty.properties"
                        class="!ml-12 !list-none border-l border-gray-400"
                    >
                        <li
                            v-for="(
                                subItemProperty, subItemPropertyKey
                            ) in itemProperty.properties"
                            :key="subItemPropertyKey"
                            class="pl-2"
                        >
                            <span class="font-bold text-gray-800"
                                >${{ variableKey }}[ ]->{{
                                    itemPropertyKey
                                }}->{{ subItemPropertyKey }}</span
                            >
                            <span class="ml-2 text-xs text-slate-500">{{
                                subItemProperty.type
                            }}</span>
                            <span class="ml-2 text-xs text-slate-400">{{
                                subItemProperty.description
                            }}</span>
                        </li>
                    </ul>
                </li>
            </ul>
        </li>
    </ul>
</template>
